import { type TeamMember } from "@fitness-app/data-models";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";
import { getUserClaims } from "../../user/selectors";
import {
  subscribeToTeamMemberFailed,
  subscribeToTeamMemberPending,
  subscribeToTeamMemberSuccess,
  updateTeamMember,
} from "../reducer";
import { fetchTeamMember } from "./fetchTeamMember";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToTeamMember =
  (memberIdFromParam?: string): AppThunk =>
  async (dispatch, getState, { db, auth, subscriptions }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "teamMember");

    try {
      dispatch(subscribeToTeamMemberPending());
      const memberId = getUserClaims(getState()).memberId || memberIdFromParam;

      if (!memberId) {
        dispatch(subscribeToTeamMemberFailed());
        return;
      }

      await dispatch(fetchTeamMember(memberId));

      const { id } = await getLoggedUser(auth);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "teamMember", claimedSlot)) {
        return;
      }

      const channelName = `team-member-${id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeTeamMemberListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "teamMember", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.teamMemberListChannelName = channelName;
      subscriptions.realtimeTeamMemberListChannel = channel;

      subscriptions.realtimeTeamMemberListChannel
        .on<TeamMember>(
          "postgres_changes",
          { event: "*", schema: "public", table: "team_member", filter: `id=eq.${memberId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateTeamMember(payload.new));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToTeamMemberSuccess());
          }
        });
    } catch {
      dispatch(subscribeToTeamMemberFailed());
    }
  };
